import React from 'react'
import { GetServerSideProps } from 'next'
import Head from 'next/head'
import { useRouter } from 'next/router'

import { API } from '../../../../revelry_api'
import EventTrackingModel from '../../../../components/models/EventTrackingModel'
import { FormValues } from '../../../../forms/ObituaryMemoryCommentForm'
import FourOFour from '../../../../components/pages/FourOFour'
import {
  FuneralHome,
  LifePageNavItem,
  ObituaryMemory as IObituaryMemory,
  ObituarySnippet,
  Reactable,
  ReactionByID,
  VisitorUser,
} from '../../../../types'
import { FuneralHomeFooter } from '../../../../components/FuneralHomeFooter'
import LifePageLayout from '../../../../components/LifePageLayout'
import MemoriesPage from '../../../../components/pages/MemoriesPage'
import ObituaryMemoryModal from '../../../../components/modals/ObituaryMemoryModal'
import Obituary from '../../../../components/models/Obituary'
import { routes } from '../../../../routes'
import { storage, NULL_VISITOR_USER } from '../../../../storage'
import useSetBackgroundColor from '../../../../hooks/useSetBackgroundColor'

interface Props {
  funeralHome: FuneralHome | null
  obituary: ObituarySnippet | null
  selectedMemory: IObituaryMemory | null
}

const INITIAL_VALUES = {
  content: '',
  email: '',
  has_candle: false,
  location: '',
  name: '',
}

const PAGE_LIMIT = 10

const ObituaryMemoriesPage: React.FC<Props> = ({ funeralHome, obituary, selectedMemory }) => {
  useSetBackgroundColor('#f0f0f0')

  const router = useRouter()
  const [modalMemory, setModalMemory] = React.useState<IObituaryMemory | null>(selectedMemory)
  const [memories, setMemories] = React.useState<{ [memoryID: string]: IObituaryMemory }>({})
  const [page, setPage] = React.useState(1)
  const [canLoadMore, setCanLoadMore] = React.useState(false)
  const [visitorUser, setVisitorUser] = React.useState<VisitorUser>(NULL_VISITOR_USER)
  const [reactionsByMemoryID, setReactionsByMemoryID] = React.useState<ReactionByID>({})
  const [reactionsByCommentID, setReactionsByCommentID] = React.useState<ReactionByID>({})
  const [userMemories, setUserMemories] = React.useState<{ [memoryID: string]: IObituaryMemory }>(
    {},
  )
  const [formOpen, setFormOpen] = React.useState(false)
  const obituaryMemoryCommentFormInitialValues = {
    content: '',
    email: visitorUser.email,
    name: visitorUser.name,
  }
  const onObituaryMemoryCommentFormSubmit = async (formValues: FormValues): Promise<void> => {
    const { content, ...visitorUserParams } = formValues
    const userMemory = userMemories[modalMemory.id]
    const memory = memories[modalMemory.id]
    storage.setVisitorUser(visitorUserParams)
    setVisitorUser(visitorUserParams)
    await API.obituaryMemoryComments.create(modalMemory.id, {
      content,
      visitor_user: visitorUserParams,
    })

    EventTrackingModel.trackObituaryMemoryCommentShare()

    const apiMemory = await API.obituaryMemories.load(obituary.id, modalMemory.id)

    userMemory && setUserMemories({ ...userMemories, [apiMemory.id]: apiMemory })
    memory && setMemories({ ...memories, [apiMemory.id]: apiMemory })
    setModalMemory(apiMemory)
  }
  const onLike = async (memoryID: number, newUser?: VisitorUser): Promise<void> => {
    let visitorUserEmail = visitorUser.email
    if (!visitorUserEmail && !newUser) {
      return
    }

    const reactionID = reactionsByMemoryID[memoryID]
    const userMemory = userMemories[memoryID]

    if (reactionID) {
      await API.reactions.destroy(reactionID)

      EventTrackingModel.trackUnlikeMemory()

      if (userMemory) {
        setUserMemories({
          ...userMemories,
          [memoryID]: { ...userMemory, like_count: userMemory.like_count - 1 },
        })

        if (userMemory.id === modalMemory?.id) {
          setModalMemory({ ...modalMemory, like_count: modalMemory.like_count - 1 })
        }
      } else {
        const memory = memories[memoryID]

        setMemories({
          ...memories,
          [memoryID]: { ...memory, like_count: memory.like_count - 1 },
        })

        if (memory.id === modalMemory?.id) {
          setModalMemory({ ...modalMemory, like_count: modalMemory.like_count - 1 })
        }
      }
    } else {
      if (!visitorUserEmail && newUser) {
        await API.visitorUsers.create(newUser)

        storage.setVisitorUser(newUser)
        setVisitorUser(newUser)
        visitorUserEmail = newUser.email
      }

      await API.reactions.create({
        reactable_type: Reactable.OBITUARY_MEMORY,
        reactable_id: memoryID,
        visitor_user_email: visitorUserEmail,
      })

      EventTrackingModel.trackLikeMemory()

      if (userMemory) {
        setUserMemories({
          ...userMemories,
          [memoryID]: { ...userMemory, like_count: userMemory.like_count + 1 },
        })

        if (userMemory.id === modalMemory?.id) {
          setModalMemory({ ...modalMemory, like_count: modalMemory.like_count + 1 })
        }
      } else {
        const memory = memories[memoryID]

        setMemories({
          ...memories,
          [memoryID]: { ...memory, like_count: memory.like_count + 1 },
        })

        if (memory.id === modalMemory?.id) {
          setModalMemory({ ...modalMemory, like_count: modalMemory.like_count + 1 })
        }
      }
    }

    const reactions = await API.reactions.loadForCase(
      obituary.id,
      visitorUserEmail,
      Reactable.OBITUARY_MEMORY,
    )
    setReactionsByMemoryID(reactions)
  }
  const onLikeComment = async (commentID: number, newUser?: VisitorUser) => {
    let visitorUserEmail = visitorUser.email
    if (!visitorUserEmail && !newUser) {
      return
    }

    const reactionID = reactionsByCommentID[commentID]
    const userMemory = userMemories[modalMemory.id]
    const memory = memories[modalMemory.id]

    if (reactionID) {
      await API.reactions.destroy(reactionID)

      EventTrackingModel.trackUnlikeComment()
    } else {
      if (!visitorUserEmail && newUser) {
        await API.visitorUsers.create(newUser)

        storage.setVisitorUser(newUser)
        setVisitorUser(newUser)
        visitorUserEmail = newUser.email
      }

      await API.reactions.create({
        reactable_type: Reactable.OBITUARY_MEMORY_COMMENT,
        reactable_id: commentID,
        visitor_user_email: visitorUserEmail,
      })
      EventTrackingModel.trackLikeComment()
    }

    const [apiMemory, reactions] = await Promise.all([
      API.obituaryMemories.load(obituary.id, modalMemory.id),
      API.reactions.loadForCase(obituary.id, visitorUserEmail, Reactable.OBITUARY_MEMORY_COMMENT),
    ])

    userMemory && setUserMemories({ ...userMemories, [apiMemory.id]: apiMemory })
    memory && setMemories({ ...memories, [apiMemory.id]: apiMemory })
    setModalMemory(apiMemory)
    setReactionsByCommentID(reactions)
  }
  const loadMemories = async (): Promise<void> => {
    const apiMemories = await API.obituaryMemories.loadAll(obituary.id, { limit: PAGE_LIMIT, page })

    setCanLoadMore(apiMemories.length === PAGE_LIMIT)
    setPage(page + 1)
    setMemories({
      ...memories,
      ...Object.fromEntries(apiMemories.map((memory) => [memory.id, memory])),
    })
  }

  React.useEffect(() => {
    const storedVisitorUser = storage.getVisitorUser()
    setVisitorUser(storedVisitorUser)
    Promise.all([
      API.reactions.loadForCase(obituary.id, storedVisitorUser.email, Reactable.OBITUARY_MEMORY),
      API.reactions.loadForCase(
        obituary.id,
        storedVisitorUser.email,
        Reactable.OBITUARY_MEMORY_COMMENT,
      ),
    ]).then(([memoryReactions, commentReactions]) => {
      setReactionsByMemoryID(memoryReactions)
      setReactionsByCommentID(commentReactions)
    })

    loadMemories()
  }, [])

  React.useEffect(() => {
    setModalMemory(selectedMemory)
  }, [selectedMemory])

  if (!obituary) {
    return <FourOFour />
  }

  return (
    <>
      <Head>
        <title>
          {Obituary.deceasedFullName(obituary)} Memories - {funeralHome?.name}
        </title>

        {!!selectedMemory && <meta name="robots" content="noindex" />}

        <meta
          name="description"
          content={`See memories shared for ${Obituary.deceasedFullName(
            obituary,
          )}. Upload memories & photos, view funeral service information, and send condolences or flowers.`}
          key="description"
        />
      </Head>

      <LifePageLayout activeNavItem={LifePageNavItem.MEMORIES} obituary={obituary}>
        <MemoriesPage
          canLoadMore={canLoadMore}
          formOpen={formOpen}
          initialValues={{ ...INITIAL_VALUES, ...visitorUser }}
          memories={memories}
          obituary={obituary}
          onLike={onLike}
          onLoadMore={() => loadMemories()}
          onSetFormOpen={() => setFormOpen(true)}
          onSubmit={async (values) => {
            const { content, has_candle, image, ...visitorUserParams } = values
            const params = {
              content,
              has_candle,
              has_flowers: false,
              has_trees: false,
              image,
              visitor_user: visitorUserParams,
            }
            storage.setVisitorUser(visitorUserParams)
            setFormOpen(false)
            setVisitorUser(visitorUserParams)
            const response = await API.obituaryMemories.create(obituary.id, params)

            EventTrackingModel.trackObituaryMemoryShare()

            setUserMemories({ ...userMemories, [response.id]: response })
          }}
          reactionsByMemoryID={reactionsByMemoryID}
          userMemories={userMemories}
          visitorUser={visitorUser}
        />

        {modalMemory && (
          <ObituaryMemoryModal
            hasReaction={Boolean(reactionsByMemoryID[modalMemory.id])}
            obituary={obituary}
            obituaryMemory={modalMemory}
            obituaryMemoryCommentFormInitialValues={obituaryMemoryCommentFormInitialValues}
            onClose={() => router.push(routes.memories(obituary))}
            onLike={onLike}
            onLikeComment={onLikeComment}
            onObituaryMemoryCommentFormSubmit={onObituaryMemoryCommentFormSubmit}
            reactionsByCommentID={reactionsByCommentID}
            visitorUser={visitorUser}
          />
        )}
      </LifePageLayout>

      <FuneralHomeFooter />
    </>
  )
}

export const getServerSideProps: GetServerSideProps = async (req) => {
  try {
    const slugArray = (req.params.slug as string).split('-')
    const obituaryID = slugArray[slugArray.length - 1]
    const [funeralHome, obituary, selectedMemory] = await Promise.all([
      API.funeralHomes.load(),
      API.obituaries.load(obituaryID),
      req.query.memory
        ? API.obituaryMemories.load(obituaryID, req.query.memory as string)
        : Promise.resolve(null),
    ])

    return { props: { funeralHome, obituary, selectedMemory } }
  } catch (e) {
    return { props: { funeralHome: null, obituary: null, selectedMemory: null } }
  }
}

export default ObituaryMemoriesPage
